"use client"

import { useEffect, useState } from "react"
import { DeveloperCard } from "@/components/DeveloperCard"
import { fetchCoders, type CodersParams, type Coder } from "@/lib/api/coders"

export function ProgrammerListClient({ initialParams }: { initialParams: CodersParams }) {
  const [coders, setCoders] = useState<Coder[]>([])
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 20,
    total_pages: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = async (params: CodersParams) => {
    setLoading(true)
    setError(null)
    try {
      console.log("Fetching coders with params:", params)
      const data = await fetchCoders(params)
      console.log("Received data:", data)
      setCoders(data.data)
      setPagination(data.pagination)
    } catch (err) {
      console.error("Failed to fetch coders:", err)
      const errorMessage = err instanceof Error ? err.message : "加载失败，请稍后重试"
      setError(errorMessage + " (请检查后端服务是否运行在 http://115.159.77.126:8000)")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    console.log("ProgrammerListClient mounted, initialParams:", initialParams)
    loadData(initialParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const params: CodersParams = {
      careerTag2: formData.get("careerTag2") ? Number(formData.get("careerTag2")) : -1,
      industryTag: formData.get("industryTag") ? Number(formData.get("industryTag")) : -1,
      city: formData.get("city") ? Number(formData.get("city")) : -1,
      isJobSeeking: formData.get("isJobSeeking") ? Number(formData.get("isJobSeeking")) : -1,
      page: 1,
    }
    loadData(params)
  }

  const handlePageChange = (page: number) => {
    const params: CodersParams = {
      ...initialParams,
      page,
    }
    loadData(params)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={() => loadData(initialParams)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            重新加载
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <header className="px-6 py-8 md:px-8 lg:w-[80%] lg:mx-auto lg:px-0">
        <h1 className="text-2xl font-semibold text-gray-900 md:text-3xl">
          程序员(开发者)
        </h1>
      </header>

      {/* Main Content */}
      <div className="max-w-full lg:max-w-[80%] mx-auto px-4 sm:px-6 lg:px-0 pb-8">
        {/* Filter Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">职业方向:</label>
              <select
                name="careerTag2"
                defaultValue={initialParams.careerTag2 ?? -1}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              >
                <option value="-1">全部</option>
                <option value="101001">Java</option>
                <option value="101002">Python</option>
                <option value="101003">PHP</option>
                <option value="102001">Web前端</option>
                <option value="103002">Android</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">行业:</label>
              <select
                name="industryTag"
                defaultValue={initialParams.industryTag ?? -1}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              >
                <option value="-1">全部</option>
                <option value="601">电商</option>
                <option value="605">金融</option>
                <option value="609">人工智能</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">城市:</label>
              <select
                name="city"
                defaultValue={initialParams.city ?? -1}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              >
                <option value="-1">全部</option>
                <option value="101">北京</option>
                <option value="201">上海</option>
                <option value="301">深圳</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">求职状态:</label>
              <select
                name="isJobSeeking"
                defaultValue={initialParams.isJobSeeking ?? -1}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              >
                <option value="-1">全部</option>
                <option value="1">求职中</option>
                <option value="2">考虑求职</option>
                <option value="3">不求职</option>
                <option value="0">未设置</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-1.5 rounded text-sm hover:bg-blue-700 disabled:bg-blue-400"
            >
              {loading ? "加载中..." : "筛选"}
            </button>
          </form>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-gray-600">
          共找到 <span className="font-medium text-gray-900">{pagination.total}</span> 位开发者
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Developers Grid */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {coders.map((coder) => (
              <DeveloperCard
                key={coder.id}
                id={coder.id}
                nickname={coder.nickname}
                avatar={coder.avatar}
                workYears={coder.work_years}
                successRate={coder.success_rate}
                recentlyActiveString={coder.recently_active_string}
                isJobSeeking={coder.is_job_seeking}
                freelanceStatus={coder.freelance_status}
                cityString={coder.city_string}
                skills={coder.skills.map(s => ({
                  skill_tag: s.skill_tag,
                  skill_tag_string: s.skill_tag_string,
                  is_main: s.is_main
                }))}
                industryExperiences={coder.industry_experiences.map(i => ({
                  industry_tag: i.industry_tag,
                  industry_tag_string: i.industry_tag_string
                }))}
                careerTags={coder.career_tags.map(t => ({
                  career_tag1: t.career_tag1,
                  career_tag1_string: t.career_tag1_string,
                  career_tag2: t.career_tag2,
                  career_tag2_string: t.career_tag2_string
                }))}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mt-8 flex justify-center items-center gap-2">
            {pagination.page > 1 && (
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:bg-gray-100"
              >
                上一页
              </button>
            )}

            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                const pageNum = i + 1
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className={`px-3 py-2 rounded text-sm ${
                      pageNum === pagination.page
                        ? "bg-blue-600 text-white"
                        : "border border-gray-300 hover:bg-gray-50"
                    } disabled:bg-gray-100`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            {pagination.page < pagination.total_pages && (
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:bg-gray-100"
              >
                下一页
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
